/**
 * The languages the app knows, in one place. Plain data, so vite.config.ts and the build
 * scripts can read it too (scripts/vite-plugin-seo.ts stamps the shipped list on `<html>` for
 * the boot script in index.html).
 *
 * A language is *shipped* once `src/i18n/locales/<code>.json` exists: the menu, detection and
 * the profile setting only ever offer shipped ones, so a translation goes live by adding its file.
 */

export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'es', name: 'Español' },
  { code: 'pt-BR', name: 'Português (Brasil)' },
  { code: 'ru', name: 'Русский' },
  { code: 'vi', name: 'Tiếng Việt' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

/** What a visitor sees when nothing they send matches a shipped language. */
export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** The language's own name, as the menu lists it. */
export function languageName(code: LanguageCode): string {
  return LANGUAGES.find((language) => language.code === code)?.name ?? code;
}

/**
 * The shipped language a BCP-47 tag means, or `null`: an exact match first (`pt-BR`), then the
 * primary subtag (`es-MX` → `es`, `pt-PT` → `pt-BR`). Case does not matter.
 */
export function matchLanguage(
  tag: string | null | undefined,
  shipped: readonly LanguageCode[],
): LanguageCode | null {
  if (!tag) return null;
  const wanted = tag.trim().toLowerCase().replace('_', '-');
  if (!wanted) return null;
  const exact = shipped.find((code) => code.toLowerCase() === wanted);
  if (exact) return exact;
  const primary = wanted.split('-')[0];
  return shipped.find((code) => code.toLowerCase().split('-')[0] === primary) ?? null;
}
